'use client';

import { resolveEnhancerBrowserUploadAccept } from '@/ai/image/upload-image-preprocess-policy';
import { CmsEditorialIcon } from '@/cms/components/blocks/shared/cms-editorial-icon';
import type { WorkspaceToolPrefillFieldView } from '@/cms/components/blocks/workspace/workspace-1/contracts/tool-view';
import type { WorkspacePrefillFormModel } from '@/cms/components/blocks/workspace/workspace-1/prefill/workspace-prefill-model';
import type { WorkspacePrefillRequestV1 } from '@/cms/components/blocks/workspace/workspace-prefill-contract';
import {
  useCmsComponentBlockInstanceId,
  useCmsComponentButton,
} from '@/cms/components/component-button-action';
import { cn } from '@/lib/ui/utils';
import type { DragEvent } from 'react';
import { useRef, useState } from 'react';

type HeroWorkspaceLaunchDropzoneProps = {
  className?: string;
  dropzoneClassName?: string;
  contentClassName?: string;
  iconWrapClassName?: string;
  iconClassName?: string;
  copyClassName?: string;
  titleClassName?: string;
  buttonClassName?: string;
  uploadTitle: string;
  prefillForm: WorkspacePrefillFormModel;
  primaryImageField: WorkspaceToolPrefillFieldView;
};

export function HeroWorkspaceLaunchDropzone({
  className,
  dropzoneClassName,
  contentClassName,
  iconWrapClassName,
  iconClassName,
  copyClassName,
  titleClassName,
  uploadTitle,
  prefillForm,
  primaryImageField,
}: HeroWorkspaceLaunchDropzoneProps) {
  const blockInstanceId = useCmsComponentBlockInstanceId();
  const inputId = `cms-hero-workspace-upload-${encodeURIComponent(
    blockInstanceId
  )}-${encodeURIComponent(primaryImageField.id)}`;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const buttonBinding = useCmsComponentButton('workspaceLaunch');

  function launchWithFiles(files: File[]) {
    const request: WorkspacePrefillRequestV1 = {
      version: 1,
      valuesByFieldId: {},
      filesByFieldId: { [primaryImageField.id]: files },
    };
    if (!buttonBinding.managed || !buttonBinding.button) {
      throw new Error(
        `Hero-1 is missing its required Workspace action for ${prefillForm.toolLabel}.`
      );
    }

    buttonBinding.button.trigger({ workspacePrefill: request });
  }

  function handleDrop(event: DragEvent<HTMLElement>) {
    event.preventDefault();
    setIsDragging(false);
    const files = Array.from(event.dataTransfer.files ?? []);
    if (files.length > 0) {
      launchWithFiles(files);
    }
  }

  return (
    <div className={className}>
      <input
        ref={inputRef}
        id={inputId}
        name="cms-hero-workspace-upload"
        type="file"
        className="sr-only"
        accept={resolveEnhancerBrowserUploadAccept(
          primaryImageField.imageConstraints?.allowedMimeTypes
        )}
        multiple={
          primaryImageField.allowBatch ||
          (primaryImageField.imageConstraints?.maxCount ?? 1) > 1
        }
        aria-label={uploadTitle}
        onChange={(event) => {
          const files = Array.from(event.currentTarget.files ?? []);
          event.currentTarget.value = '';
          if (files.length > 0) {
            launchWithFiles(files);
          }
        }}
      />
      <button
        type="button"
        className={cn(dropzoneClassName, isDragging && 'is-dragging')}
        onClick={() => inputRef.current?.click()}
        onDragEnter={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        <div className={contentClassName}>
          <span className={iconWrapClassName} aria-hidden="true">
            <CmsEditorialIcon name="upload" className={iconClassName} />
          </span>
          <div className={copyClassName}>
            <p className={titleClassName}>{uploadTitle}</p>
          </div>
        </div>
      </button>
    </div>
  );
}
